import { makeAutoObservable, observable, action } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

// Single source of truth for column types and their validation rules
export const ColumnTypes = {
    // Text
    SINGLE_LINE_TEXT: {
        id: 'SINGLE_LINE_TEXT',
        label: 'Single-line Text',
        validate: (value) => typeof value === 'string',
        icon: '📝',
        supportsRichText: false
    },
    MULTI_LINE_TEXT: {
        id: 'MULTI_LINE_TEXT',
        label: 'Multi-line Text',
        validate: (value) => typeof value === 'string' || (Array.isArray(value) && value.every(item => typeof item === 'object' && item !== null && 'text' in item)),
        icon: '📄',
        supportsRichText: true
    },
    EMAIL: {
        id: 'EMAIL',
        label: 'Email',
        validate: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
        icon: '🔗',
        supportsRichText: false
    },
    URL: {
        id: 'URL',
        label: 'URL',
        validate: (value) => {
            try {
                new URL(value);
                return true;
            } catch {
                return false;
            }
        },
        icon: '🔗'
    },

    // Dates
    DATE: {
        id: 'DATE',
        label: 'Date',
        validate: (value) => !isNaN(Date.parse(value)),
        icon: '📅'
    },
    START_DATE: {
        id: 'START_DATE',
        label: 'Start Date',
        validate: (value) => !isNaN(Date.parse(value)),
        icon: '🏁'
    },
    END_DATE: {
        id: 'END_DATE',
        label: 'End Date',
        validate: (value) => !isNaN(Date.parse(value)),
        icon: '🏆',
        supportsRichText: false
    },

    // Managed by data validation in cells - editable
    SELECT: {
        id: 'SELECT',
        label: 'Select',
        validate: (value, options) => !value || options.includes(value),
        hasCustomValidation: true,
        hasConditionalFormatting: true,
        conditionalFormattingType: 'cellIs',
        defaultOptions: [],
        icon: '📋',
        supportsRichText: false,
        supportsConditionalFormatting: true,
    },
    STATUS: {
        id: 'STATUS',
        label: 'Status',
        validate: (value, options) => !value || options.includes(value),
        hasCustomValidation: true,
        defaultOptions: ['Not Started', 'In Progress', 'Done'],
        hasConditionalFormatting: true,
        conditionalFormattingType: 'cellIs',
        icon: '🔄',
        supportsRichText: false,
        supportsConditionalFormatting: true,
    },
    PERSON: {
        id: 'PERSON',
        label: 'Person',
        validate: (value, options) => !value || options.includes(value),
        hasCustomValidation: true,
        defaultOptions: [],
        icon: '👤',
        supportsRichText: false
    },

    // Managed by data validation in cells - non-editable
    RATING: {
        id: 'RATING',
        label: 'Rating',
        validate: (value) => Number.isInteger(value) && value >= 0 && value <= 5,
        hasConditionalFormatting: true,
        conditionalFormattingType: 'colorScale',
        defaultOptions: ['0', '1', '2', '3', '4', '5'],
        icon: '⭐',
        supportsRichText: false
    },

    // Booleans
    CHECKBOX: {
        id: 'CHECKBOX',
        label: 'Checkbox',
        validate: (value) => typeof value === 'boolean',
        hasConditionalFormatting: true,
        conditionalFormattingType: 'cellIs',
        defaultOptions: ['TRUE', 'FALSE'],
        icon: '✅',
        supportsRichText: false,
        supportsConditionalFormatting: true,
    },

    // Numbers
    PERCENTAGE: {
        id: 'PERCENTAGE',
        label: 'Percentage',
        validate: (value) => !isNaN(value) && value >= 0 && value <= 100,
        icon: '%',
        supportsRichText: false,
        supportsConditionalFormatting: false,
    },
    NUMBER: {
        id: 'NUMBER',
        label: 'Number',
        validate: (value) => !isNaN(value),
        icon: '#',
        supportsRichText: false,
        supportsConditionalFormatting: false,
    },
};

export const ViewTypes = {
    TABLE: {
        id: 'TABLE',
        label: 'Table',
        icon: '📊',
        requiredColumns: [], // Table view can handle any columns
        optionalColumns: [],
        description: 'View and edit data in a spreadsheet format'
    },
    LIST: {
        id: 'LIST',
        label: 'List',
        icon: '📝',
        requiredColumns: [
            { type: ColumnTypes.SINGLE_LINE_TEXT, name: 'Task' }
        ],
        optionalColumns: [
            { type: ColumnTypes.CHECKBOX, name: 'Done' },
            { type: ColumnTypes.DATE, name: 'Due Date' },
            { type: ColumnTypes.PERSON, name: 'Assignee' },
            { type: ColumnTypes.STATUS, name: 'Priority' }
        ],
        description: 'Organize tasks in a checklist format'
    },
    BOARD: {
        id: 'BOARD',
        label: 'Board',
        icon: '📋',
        requiredColumns: [
            { type: ColumnTypes.STATUS, name: 'Status' }
        ],
        optionalColumns: [
            { type: ColumnTypes.PERSON, name: 'Assignee' },
            { type: ColumnTypes.DATE, name: 'Due Date' }
        ],
        description: 'Kanban board view for managing workflows'
    },
    CALENDAR: {
        id: 'CALENDAR',
        label: 'Calendar',
        icon: '📅',
        requiredColumns: [
            { type: ColumnTypes.DATE, name: 'Date' }
        ],
        optionalColumns: [
            { type: ColumnTypes.END_DATE, name: 'End Date' }
        ],
        description: 'View items on a calendar'
    },
    TIMELINE: {
        id: 'TIMELINE',
        label: 'Timeline',
        icon: '⏱️',
        requiredColumns: [
            { type: ColumnTypes.START_DATE, name: 'Start Date' },
            { type: ColumnTypes.END_DATE, name: 'End Date' }
        ],
        optionalColumns: [
            { type: ColumnTypes.STATUS, name: 'Status' },
            { type: ColumnTypes.PERSON, name: 'Assignee' }
        ],
        description: 'Visualize items on a timeline'
    }
};

export class Column {
    id = uuidv4();
    heading = '';
    type = ColumnTypes.SINGLE_LINE_TEXT;
    width = 100; // Default width
    validationOptions = observable.array([]); // Make observable array for dropdown options
    optionStyles = observable.map({}); // Observable map for { optionValue: styleObject }

    constructor(heading, type = ColumnTypes.SINGLE_LINE_TEXT, validationOptions = [], width = 100) {
        makeAutoObservable(this, {
            id: false, // IDs are typically not observable/mutable after creation
            optionStyles: observable,
            setOptionStyle: action,
            removeOptionStyle: action,
            clearOptionStyles: action, // Mark new method as action
            setValidationOptions: action, // Ensure this is an action
        });
        this.heading = heading;
        this.type = type || ColumnTypes.SINGLE_LINE_TEXT; // Ensure type is never null/undefined
        this.width = width;
        this.setValidationOptions(validationOptions || []); // Use the action to set initial options
    }

    // Action to set validation options
    setValidationOptions(options) {
        if (this.type.hasCustomValidation) {
            // Ensure options are unique strings
            const uniqueOptions = [...new Set(options.map(opt => String(opt).trim()))];
            this.validationOptions.replace(uniqueOptions);

            // Remove styles for options that no longer exist
            const currentStyles = Array.from(this.optionStyles.keys());
            currentStyles.forEach(optionValue => {
                if (!uniqueOptions.includes(optionValue)) {
                    this.removeOptionStyle(optionValue);
                }
            });
        } else {
            this.validationOptions.clear();
            this.clearOptionStyles(); // Clear styles if not a custom validation type
        }
    }

    // Action to set a specific style for an option
    setOptionStyle(optionValue, styleObject) {
        if (this.type.hasCustomValidation && this.validationOptions.includes(optionValue)) {
            this.optionStyles.set(optionValue, styleObject);
        } else {
            console.warn(`Cannot set style for option "${optionValue}". It's either not a valid option for column "${this.heading}" or the column type doesn't support custom styles.`);
        }
    }

    // Action to remove a style for an option
    removeOptionStyle(optionValue) {
        this.optionStyles.delete(optionValue);
    }

    // Action to clear all option styles
    clearOptionStyles() {
        this.optionStyles.clear();
    }

    // Check if a value is valid based on current options (if applicable)
    isValidValue(value) {
        if (!this.type.hasCustomValidation || this.validationOptions.length === 0) {
            return true; // No validation applicable
        }
        // Allow null/empty values
        if (value === null || value === undefined || String(value).trim() === '') {
            return true;
        }
        return this.validationOptions.includes(String(value));
    }

    setWidth(newWidth) {
        // Add validation/clamping if needed
        this.width = newWidth;
    }
}

export class Sheet {
    constructor(name) {
        this.id = uuidv4();
        this.name = name;
        this.columns = [];
        this.rows = new Map();
        this.availableViews = new Set(['TABLE']); // Start with just table view
        
        makeAutoObservable(this, {
            id: false,
            rows: true
        });
    }

    isValidColumnHeading(heading) {
        return heading && 
               typeof heading === 'string' && 
               heading.trim().length > 0 &&
               !this.columns.some(col => col.heading === heading);
    }

    addView(viewType) {
        if (ViewTypes[viewType]) {
            this.availableViews.add(viewType);
        }
    }

    removeView(viewType) {
        if (viewType !== 'TABLE') { // Don't allow removing the table view
            this.availableViews.delete(viewType);
        }
    }

    hasRequiredColumnsForView(viewType) {
        if (!ViewTypes[viewType]) return false;
        
        const requiredColumns = ViewTypes[viewType].requiredColumns;
        return requiredColumns.every(required => 
            this.columns.some(col => col.type.id === required.type.id)
        );
    }

    getMissingColumnsForView(viewType) {
        if (!ViewTypes[viewType]) return [];
        
        const requiredColumns = ViewTypes[viewType].requiredColumns;
        return requiredColumns.filter(required => 
            !this.columns.some(col => col.type.id === required.type.id)
        );
    }
}